import { Button } from "@sellar-io/components";
import { PriceTierDiscountType } from "@sellar-io/api-v2/types";
import { useForm } from "react-hook-form";
import { v4 } from "uuid";
import { Alert } from "../../../../../components/alert/Alert";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
  DialogCTA,
} from "../../../../../components/modal/Dialog";
import { apiV2AssertError } from "../../../../../hooks/api/v2/apiV2Request";
import { usePriceTierDiscountDelete } from "../../../../../hooks/api/v2/priceTierDiscount/usePriceTierDiscountDelete";
import { usePriceTierDiscountUpdate } from "../../../../../hooks/api/v2/priceTierDiscount/usePriceTierDiscountUpdate";
import { formatPriceApiToInput } from "../../../../../hooks/usePriceFormat";
import {
  PriceTierDiscountForm,
  PriceTierDiscountFormValues,
} from "./PriceTierDiscountForm";

const formId = v4();

export const PriceTierDiscountDialogUpdate = ({
  priceTierDiscount,
  active,
  onDismiss,
}: {
  priceTierDiscount: Omit<
    PriceTierDiscountType,
    "createdAt" | "updatedAt" | "deletedAt" | "VolumeDiscountsId"
  >;
  active: boolean;
  onDismiss: () => void;
}) => {
  const discountByType =
    priceTierDiscount.minimumBasketValue > 0
      ? "minimumBasketValue"
      : "minimumOrderQuantity";

  const itemType = priceTierDiscount.itemType ?? "any";

  const amount =
    priceTierDiscount.type === "percentage"
      ? priceTierDiscount.amount.toString()
      : formatPriceApiToInput(priceTierDiscount.amount);

  const minimumBasketValue =
    discountByType === "minimumBasketValue"
      ? Number.parseFloat(
          formatPriceApiToInput(priceTierDiscount.minimumBasketValue),
        )
      : undefined;

  const form = useForm<PriceTierDiscountFormValues>({
    shouldUseNativeValidation: true,
    defaultValues: {
      ...priceTierDiscount,
      discountByType,
      itemType,
      amount,
      minimumBasketValue,
    },
  });

  const priceTierDiscountUpdateMutation = usePriceTierDiscountUpdate(
    priceTierDiscount.id,
  );
  const priceTierDiscountDeleteMutation = usePriceTierDiscountDelete(
    priceTierDiscount.id,
  );

  return (
    <Dialog active={active} onDismiss={onDismiss}>
      <DialogNavigation
        title={`Edit price tier discount`}
        leftIcon={"CHEVRON_DOWN"}
        onClickLeftIcon={onDismiss}
      />
      <DialogContent>
        <PriceTierDiscountForm
          form={form}
          formId={formId}
          onSave={(data) => {
            if (data.amount === null) throw new Error("Amount not defined");
            if (data.minimumBasketValue === null)
              throw new Error("Minimum basket price not defined");

            priceTierDiscountUpdateMutation.mutate(data, {
              onSuccess: onDismiss,
            });
          }}
        />
        {priceTierDiscountUpdateMutation.isError ? (
          <Alert
            message={
              apiV2AssertError(priceTierDiscountUpdateMutation.error)?.response
                ?.data.message ?? "Unknown error"
            }
          ></Alert>
        ) : null}
      </DialogContent>
      <DialogCTA>
        <Button
          color={"red"}
          loading={priceTierDiscountDeleteMutation.isPending}
          onClick={() =>
            priceTierDiscountDeleteMutation.mutate(undefined, {
              onSuccess: () => onDismiss(),
            })
          }
        >
          Delete
        </Button>
        <Button
          loading={priceTierDiscountUpdateMutation.isPending}
          form={formId}
          type={"submit"}
        >
          Update
        </Button>
      </DialogCTA>
    </Dialog>
  );
};
